<script setup lang="ts">
import { bannerService } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { BANNER_CONTAINER_ID } from "~/core/constants/constants";
import type LxcError from "~/core/models/LxcError.class";
import { useFetchActiveLicenseInformation } from "~/modules/license/composables/useFetchActiveLicenseInformation";
import type { License } from "~/modules/license/models/License.interface";
import { getLicenseUploadErrors } from "~/modules/license/utils/getLicenseUploadErrors";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { isLoading, result, error, fetchActiveLicenseInformation } =
  useFetchActiveLicenseInformation({
    organizationCode: currentOrganization.value?.code,
  });

const license: Ref<License | undefined> = ref();

/**
 * Fetch the license
 */
async function fetchLicense() {
  await fetchActiveLicenseInformation(true);
  if (error.value) {
    license.value = undefined;
  } else {
    license.value = result.value;
  }
}

function onUploadError(error: LxcError) {
  if (error) {
    bannerService.error({
      message: getLicenseUploadErrors(error),
      container: `#${BANNER_CONTAINER_ID}`,
      dangerouslyUseHTMLString: true,
    });
  } else {
    bannerService.hide();
  }
}

onMounted(fetchLicense);
</script>

<template>
  <page-component
    :name="'layout.menu.parameters.features.licenseManagement'"
    :is-loading="isLoading"
    :error="error"
  >
    <license-properties
      v-if="license"
      :license="license"
      @upload-success="fetchLicense"
      @upload-error="onUploadError"
    />
    <license-export-id-card />
  </page-component>
</template>
